<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { isHrefLink, type CardHeader, type CardLink, type CardOtherLink } from './instructional_design_card_types'

const props = defineProps<{
  accentColor: string
  hoverColor: string
  imgSrc: string
  header: CardHeader
  cardDescription: string
  button: CardLink
  otherLinks: CardOtherLink[]
}>()

const { iodVisibility, effectsVisibility } = storeToRefs(useVisibilityStore())

const isEffectStatementsHeading = computed(() => effectsVisibility.value && props.header.line1.includes('Present Levels'))

const colorVariables = computed(() => ({ '--card-accent': props.accentColor, '--card-hover': props.hoverColor }))

function isShown(link: CardOtherLink): boolean {
  const isHiddenForMissingIod = link.isHiddenWithoutIod && !iodVisibility.value
  return !isHiddenForMissingIod
}

const shownOtherLinks = computed(() => props.otherLinks.filter(isShown))

const BUTTON_CLASSES = 'mb-6 mt-8 block w-full rounded-[6px] bg-[var(--card-accent)] px-4 py-[10px] text-center text-[1.25rem] font-bold leading-[1.3333] text-white no-underline shadow-[3px_3px_3px_0px_rgba(0,0,0,0.2)] hover:bg-[var(--card-hover)] focus:bg-[var(--card-hover)] max-[992px]:mt-6 max-[768px]:mb-4'
</script>

<template>
  <div :style="colorVariables">
    <div class="h-[7px] w-full bg-[var(--card-accent)]" />
    <section class="h-full border-2 border-t-0 border-[#BDBDBD]">
      <div class="mx-6 max-[480px]:mx-8">
        <div class="ml-16 h-[161px] w-[265px] pt-5 max-xl:flex max-xl:justify-center max-[992px]:ml-0 max-[992px]:w-auto max-[768px]:h-5">
          <img alt="" :src="imgSrc" class="h-[161px] max-h-full w-[265px] max-w-full max-[768px]:hidden">
        </div>
        <h3 class="mb-[10px] mt-5 flex flex-col text-[24px] font-medium leading-[1.1] max-[480px]:mt-0">
          <template v-if="isEffectStatementsHeading">
            <span><span class="font-regular">Identify </span><span class="mt-[10px] flex h-[1.9rem] items-center font-bold">Present Levels &</span></span>
            <span class="mt-[5px] flex h-[1.9rem] items-center font-bold">Effect Statements</span>
          </template>
          <template v-else>
            <span class="mb-0 font-regular">{{ header.line1 }}</span>
            <span class="mt-[10px] flex h-[1.9rem] items-center font-bold">{{ header.line2 }}</span>
          </template>
        </h3>
        <p class="leading-[1.25rem]">{{ cardDescription }}</p>
        <a v-if="isHrefLink(button)" :href="button.href" :class="BUTTON_CLASSES">
          {{ button.text }}&nbsp;
          <span class="material-icons-round align-middle text-[1rem]" aria-hidden="true">chevron_right</span>
        </a>
        <RouterLink v-else :to="button.to" :class="BUTTON_CLASSES">
          {{ button.text }}&nbsp;
          <span class="material-icons-round align-middle text-[1rem]" aria-hidden="true">chevron_right</span>
        </RouterLink>
        <div class="flex justify-center">
          <ul class="mb-[10px] flex list-disc flex-col items-start justify-start pl-0 text-[1rem]">
            <li v-for="link in shownOtherLinks" :key="link.text" class="leading-8 text-[var(--card-accent)]">
              <a v-if="isHrefLink(link)" :href="link.href" class="text-[var(--card-accent)] hover:underline">{{ link.text }}</a>
              <RouterLink v-else :to="link.to" class="text-[var(--card-accent)] hover:underline">{{ link.text }}</RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </section>
  </div>
</template>
